/** Minimal DOM helpers with write-if-changed caching (HUD updates every frame). */

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

const textCache = new WeakMap<HTMLElement, string>();
export function setText(e: HTMLElement, s: string): void {
  if (textCache.get(e) === s) return;
  textCache.set(e, s);
  e.textContent = s;
}

const styleCache = new WeakMap<HTMLElement, Map<string, string>>();
/** Set a CSS property (custom properties included) only when it changed. */
export function setStyle(e: HTMLElement, prop: string, value: string): void {
  let m = styleCache.get(e);
  if (!m) styleCache.set(e, (m = new Map()));
  if (m.get(prop) === value) return;
  m.set(prop, value);
  e.style.setProperty(prop, value);
}

export function setClass(e: HTMLElement, cls: string, on: boolean): void {
  if (e.classList.contains(cls) !== on) e.classList.toggle(cls, on);
}

export const pct = (v: number): string => `${(Math.max(0, Math.min(1, v)) * 100).toFixed(2)}%`;

/** Time of the last press handled by `onPress` (its trailing click is swallowed). */
let lastPressAt = -Infinity;
const CLICK_AFTER_PRESS_MS = 1000;

/**
 * Activate on the press, not the release: a primary-button `pointerdown` runs `handler` at once
 * (UI responds on the down stroke, like the game's action buttons). The click that follows the
 * press is swallowed, wherever it lands (a screen swap can put a new button under the pointer).
 * Keyboard activation (Enter / Space → click with detail 0) and touch taps keep using click.
 */
export function onPress<E extends HTMLElement>(target: E, handler: (ev: MouseEvent) => void): void {
  target.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.pointerType === 'touch' || (target as unknown as { disabled?: boolean }).disabled) return;
    // No focus ring / text selection / compat mouse events (the canvas never sees this press).
    e.preventDefault();
    lastPressAt = e.timeStamp;
    // Pressed look right away (preventDefault can keep :active from applying).
    target.classList.add('is-pressed');
    const up = (): void => {
      target.classList.remove('is-pressed');
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
    };
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    handler(e);
  });
  target.addEventListener('click', (e) => {
    if (e.detail > 0 && e.timeStamp - lastPressAt < CLICK_AFTER_PRESS_MS) return;
    handler(e);
  });
}
